import type { DrugManualDetail } from '@/entities/drug-data/api/drug-data.api';
import {
  REFERENCE_TAB_KEYS,
  type ReferenceTabKey,
} from '@/pages/drugs/drugFormOptions';
import {
  resolveMappingDrugCodeHira,
  resolveMappingDrugCodeMfds,
  resolveMappingEtcOtc,
  resolveMfdsEtcOtc,
  resolveMfdsItemName,
  resolveMfdsItemSeq,
  settlementFeeDrugCode,
} from '@/pages/drugs/drugCodeCopyPayload';

export interface SettlementRow {
  drugCode?: string;
  drugName?: string;
  pharmaDrugCode?: string;
  drugCodePharma?: string;
  pharmaCompanyName?: string;
  status?: string;
}

export interface DrugCreateFormValues {
  createRefYm: string;
  createDrugName: string;
  createDrugCode: string;
  createPharmaDrugCode: string;
  itemStdCode: string;
  spec: string;
  unit: string;
  reimbursementType: string;
  etcOtcType: string;
  dataSourceType: string;
  administrationRouteType: string;
  atcCode: string;
}

export const DEFAULT_DRUG_CREATE_FORM_VALUES: Omit<
  DrugCreateFormValues,
  'createRefYm'
> = {
  createDrugName: '',
  createDrugCode: '',
  createPharmaDrugCode: '',
  itemStdCode: '',
  spec: '',
  unit: '',
  reimbursementType: '비급여',
  etcOtcType: '일반',
  dataSourceType: 'ADMIN',
  administrationRouteType: '',
  atcCode: '',
};

function trimOrUndefined(value: unknown): string | undefined {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

function refYmFromFormValue(createRefYm: string | undefined): string | undefined {
  const trimmed = trimOrUndefined(createRefYm);
  if (!trimmed || trimmed.length < 6) return undefined;
  return trimmed.slice(0, 6);
}

/**
 * 서버(ruda-api#2368, RDL-170)가 "" 를 NULL 로 비우는 컬럼.
 * 수정 분기(PATCH, 또는 그 달에 같은 drugCode 행이 있는 POST)에서는 키를 빼거나 null 이면 기존 값을 유지하고,
 * "" 를 보내야 값을 지운다. refYm·drugCode 는 행을 찾는 키라 대상이 아니다.
 */
const CLEARABLE_DRUG_FIELDS = [
  'drugCodePharma',
  'drugCodeMfds',
  'drugName',
  'unitQuantity',
  'baseUnit',
  'reimbursementType',
  'etcOtcType',
  'dataSourceType',
  'administrationRouteType',
  'atcCode',
] as const;

const toInsertItem = (formValues: Partial<DrugCreateFormValues>) => ({
  refYm: refYmFromFormValue(formValues.createRefYm),
  drugCode: trimOrUndefined(formValues.createDrugCode),
  drugCodePharma: trimOrUndefined(formValues.createPharmaDrugCode),
  drugCodeMfds: trimOrUndefined(formValues.itemStdCode),
  drugName: trimOrUndefined(formValues.createDrugName),
  unitQuantity: trimOrUndefined(formValues.spec),
  baseUnit: trimOrUndefined(formValues.unit),
  reimbursementType: formValues.reimbursementType || '비급여',
  etcOtcType: formValues.etcOtcType || '일반',
  dataSourceType: formValues.dataSourceType || 'ADMIN',
  administrationRouteType:
    trimOrUndefined(formValues.administrationRouteType) ?? undefined,
  atcCode: trimOrUndefined(formValues.atcCode),
});

type DrugInsertItemFields = ReturnType<typeof toInsertItem>;

/**
 * 폼 값을 의약품 수기 입력 요청 항목으로 바꾼다.
 *
 * `initialValues`(모달을 열었을 때의 폼 값)를 넘기면, 처음엔 값이 있었는데 사용자가 지운 필드를
 * 생략하지 않고 "" 로 보낸다. 생략하면 서버 수정 분기가 기존 값을 그대로 남겨 "지웠는데 안 지워지는"
 * 문제가 생긴다. 처음부터 비어 있던 필드는 건드리지 않은 것으로 보고 지금처럼 생략한다.
 */
export function mapDrugCreateFormToInsertItem(
  formValues: Partial<DrugCreateFormValues>,
  initialValues?: Partial<DrugCreateFormValues>,
): DrugInsertItemFields {
  const item: DrugInsertItemFields = toInsertItem(formValues);
  if (!initialValues) return item;

  const initialItem = toInsertItem(initialValues);
  for (const key of CLEARABLE_DRUG_FIELDS) {
    if (item[key] === undefined && initialItem[key] !== undefined) {
      item[key] = '';
    }
  }
  return item;
}

export function mapDrugCreateFormToPatchRequest(
  formValues: Partial<DrugCreateFormValues>,
  initialValues?: Partial<DrugCreateFormValues>,
) {
  const insertItem = mapDrugCreateFormToInsertItem(formValues, initialValues);
  const { refYm, drugCode, ...bodyFields } = insertItem;
  const body = Object.fromEntries(
    Object.entries(bodyFields).filter(([, value]) => value !== undefined),
  );
  return { refYm, drugCode, body };
}

export function mapDrugManualDetailToCreateFormFields(
  detail: DrugManualDetail = {},
  settlementRow: SettlementRow = {},
): Omit<DrugCreateFormValues, 'createRefYm'> {
  const fee =
    detail.drugCodePharma ??
    settlementRow.pharmaDrugCode ??
    settlementRow.drugCodePharma ??
    settlementRow.drugCode ??
    '';

  return {
    createDrugName: detail.drugName ?? settlementRow.drugName ?? '',
    createDrugCode: detail.drugCode ?? settlementRow.drugCode ?? fee,
    createPharmaDrugCode: detail.drugCodePharma ?? fee,
    itemStdCode: detail.drugCodeMfds ?? '',
    spec: detail.unitQuantity ?? '',
    unit: detail.baseUnit ?? '',
    reimbursementType: detail.reimbursementType ?? '비급여',
    etcOtcType: detail.etcOtcType ?? '일반',
    dataSourceType: detail.dataSourceType ?? 'ADMIN',
    administrationRouteType: detail.administrationRouteType ?? '',
    atcCode: detail.atcCode ?? '',
  };
}

export function resolveEditDrugCodeFromDetail(
  detail: DrugManualDetail | null | undefined,
  settlementRow: SettlementRow,
): string | null {
  const candidates = [detail?.drugCode, settlementRow.drugCode];
  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return String(candidate).trim();
    }
  }
  return null;
}

export function validationItemToSettlementRow(item: {
  drugCode?: string;
  drugName?: string;
  pharmaDrugCode?: string;
  drugCodePharma?: string;
  pharmaCompanyName?: string;
  status?: string;
}): SettlementRow {
  const pharmaDrugCode = settlementFeeDrugCode(item) || item.drugCode;
  return {
    ...item,
    pharmaDrugCode,
    drugCodePharma: item.drugCodePharma ?? pharmaDrugCode,
  };
}

export function etcOtcSelectValueFromRaw(raw: unknown): string | undefined {
  if (raw == null || raw === '') return undefined;
  const text = String(raw).trim();
  const byPrefix = ['의약외품', '건기식', '전문', '일반'];
  for (const opt of byPrefix) {
    if (text === opt || text.startsWith(opt)) {
      return opt;
    }
  }
  return undefined;
}

export function resolveEtcOtcType(raw: unknown): string {
  return etcOtcSelectValueFromRaw(raw) ?? '일반';
}

export function resolveCopyFlowTabFromSourceTab(
  sourceTab?: string,
): ReferenceTabKey | null {
  const tab = (sourceTab ?? '').toUpperCase();
  if (tab.includes('MFDS') || tab.includes('허가') || tab.includes('LICENSE')) {
    return REFERENCE_TAB_KEYS.LICENSE;
  }
  if (tab.includes('MAPPING') || tab.includes('매핑')) {
    return REFERENCE_TAB_KEYS.MAPPING;
  }
  if (
    tab.includes('BASIC') ||
    tab.includes('기초') ||
    tab.includes('HIRA') ||
    tab.includes('DRUG')
  ) {
    return REFERENCE_TAB_KEYS.BASIC;
  }
  return null;
}

export function buildInitialCreateFormValues(params: {
  refYm: string;
  settlementRow?: SettlementRow | null;
  referenceRow?: Record<string, unknown> | null;
  copyFlowTab?: ReferenceTabKey | null;
  isEdit?: boolean;
  editInitialDetail?: DrugManualDetail | null;
}): DrugCreateFormValues {
  const {
    refYm,
    settlementRow,
    referenceRow,
    copyFlowTab = null,
    isEdit = false,
    editInitialDetail = null,
  } = params;

  const ymStr = refYm && String(refYm).length >= 6 ? String(refYm).slice(0, 6) : '';

  if (!settlementRow) {
    return {
      createRefYm: ymStr,
      ...DEFAULT_DRUG_CREATE_FORM_VALUES,
    };
  }

  const fee = settlementFeeDrugCode(settlementRow);
  const ref = referenceRow ?? {};

  let values: DrugCreateFormValues = {
    createRefYm: ymStr,
    ...DEFAULT_DRUG_CREATE_FORM_VALUES,
    administrationRouteType: '',
  };

  if (isEdit && editInitialDetail) {
    const mapped = mapDrugManualDetailToCreateFormFields(
      editInitialDetail,
      settlementRow,
    );
    values = {
      ...values,
      ...mapped,
      etcOtcType: resolveEtcOtcType(mapped.etcOtcType),
    };
  } else if (copyFlowTab === REFERENCE_TAB_KEYS.BASIC) {
    values = {
      ...values,
      createDrugName: String(ref.drugName ?? ''),
      createDrugCode: String(ref.drugCode ?? fee),
      createPharmaDrugCode: fee,
      itemStdCode: String(ref.drugCodeMfds ?? ''),
    };
  } else if (copyFlowTab === REFERENCE_TAB_KEYS.LICENSE) {
    values = {
      ...values,
      createDrugName: resolveMfdsItemName(ref),
      createDrugCode: fee,
      createPharmaDrugCode: fee,
      itemStdCode: resolveMfdsItemSeq(ref),
      etcOtcType: resolveEtcOtcType(resolveMfdsEtcOtc(ref)),
      atcCode: String(ref.atcCode ?? ''),
    };
  } else if (copyFlowTab === REFERENCE_TAB_KEYS.MAPPING) {
    values = {
      ...values,
      createDrugName: String(ref.drugName ?? ''),
      createDrugCode: resolveMappingDrugCodeHira(ref),
      createPharmaDrugCode: fee,
      itemStdCode: resolveMappingDrugCodeMfds(ref),
      etcOtcType: resolveEtcOtcType(resolveMappingEtcOtc(ref)),
      atcCode: String(ref.atcCode ?? ''),
    };
  } else {
    values = {
      ...values,
      createDrugName: settlementRow.drugName ?? '',
      createDrugCode: String(ref.drugCode ?? fee),
      createPharmaDrugCode: fee,
      itemStdCode: String(ref.drugCodeMfds ?? ''),
    };
  }

  return values;
}
